import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/flights/Header";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faUser,
  faEnvelope,
  faPhone,
  faPlaneDeparture,
  faPlaneArrival,
  faClock,
  faMoneyBill,
  faCreditCard,
  faTicket,
  faTimes,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { config } from "../../config";
import { calculateFinalPrice, formatPrice } from "../../utils/pricing";

function BookingConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const orderData = location.state?.order?.data;
  const isNewBooking = location.state?.fromBookingSummary || false;

  useEffect(() => {
    console.log("📋 Booking Confirmation - Full Data:", location.state);
  }, [location.state]);

  if (!orderData) {
    navigate("/flights/search");
    return null;
  }

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const formatDate = (dateString) => {
    return format(new Date(dateString), "EEE, MMM d, yyyy 'at' h:mm a");
  };

  const mainPassenger = orderData.passengers[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="text-center mb-8">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-green-500 text-5xl mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Order Confirmed!
            </h1>
            <p className="text-lg text-gray-600">
              <FontAwesomeIcon icon={faTicket} className="mr-2" />
              Booking Reference:{" "}
              <span className="font-semibold">
                {orderData.booking_reference}
              </span>
            </p>
          </div>

          {/* Passenger Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FontAwesomeIcon icon={faUser} className="mr-2 text-gray-800" />
              Passenger Details
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faUser}
                  className="mr-3 w-5 text-gray-500"
                />
                <span className="font-medium">Name:</span>&nbsp;
                {mainPassenger.title}. {mainPassenger.given_name}{" "}
                {mainPassenger.family_name}
              </p>
              <p className="mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faEnvelope}
                  className="mr-3 w-5 text-gray-500"
                />
                <span className="font-medium">Email:</span>&nbsp;
                {mainPassenger.email}
              </p>
              <p className="flex items-center">
                <FontAwesomeIcon
                  icon={faPhone}
                  className="mr-3 w-5 text-gray-500"
                />
                <span className="font-medium">Phone:</span>&nbsp;
                {mainPassenger.phone_number}
              </p>
            </div>
          </div>

          {/* Flight Details */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FontAwesomeIcon
                icon={faPlaneDeparture}
                className="mr-2 text-gray-800"
              />
              Flight Details
            </h2>
            {orderData.slices.map((slice, index) => (
              <div key={slice.id} className="bg-gray-50 p-4 rounded-lg mb-4">
                <h3 className="font-medium mb-4 flex items-center">
                  <FontAwesomeIcon
                    icon={index === 0 ? faPlaneDeparture : faPlaneArrival}
                    className="mr-2 text-gray-800"
                  />
                  {index === 0 ? "Outbound Flight" : "Return Flight"}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">From</p>
                    <p className="font-medium flex items-center">
                      <FontAwesomeIcon
                        icon={faPlaneDeparture}
                        className="mr-2 text-gray-500"
                      />
                      {slice.origin.city_name} ({slice.origin.iata_code})
                    </p>
                    <p className="text-sm flex items-center mt-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="mr-2 text-gray-500"
                      />
                      {formatDate(slice.segments[0].departing_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">To</p>
                    <p className="font-medium flex items-center">
                      <FontAwesomeIcon
                        icon={faPlaneArrival}
                        className="mr-2 text-gray-500"
                      />
                      {slice.destination.city_name} (
                      {slice.destination.iata_code})
                    </p>
                    <p className="text-sm flex items-center mt-1">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="mr-2 text-gray-500"
                      />
                      {formatDate(slice.segments[0].arriving_at)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 border-t pt-4">
                  <p className="text-sm flex items-center">
                    <img
                      src={slice.segments[0].marketing_carrier.logo_symbol_url}
                      alt="Airline Logo"
                      className="h-4 w-4 mr-2"
                    />
                    <span className="font-medium">Airline:</span>&nbsp;
                    {slice.segments[0].marketing_carrier.name}
                  </p>
                  <p className="text-sm mt-1">
                    <FontAwesomeIcon
                      icon={faTicket}
                      className="mr-2 text-gray-500"
                    />
                    <span className="font-medium">Flight:</span>&nbsp;
                    {slice.segments[0].marketing_carrier.iata_code}
                    {slice.segments[0].marketing_carrier_flight_number}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Payment Information */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FontAwesomeIcon
                icon={faMoneyBill}
                className="mr-2 text-gray-800"
              />
              Payment Details
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="mb-2 flex items-center">
                <FontAwesomeIcon
                  icon={faMoneyBill}
                  className="mr-3 w-5 text-gray-500"
                />
                <span className="font-medium">Total Amount:</span>
                {formatPrice(
                  calculateFinalPrice(orderData.total_amount),
                  orderData.total_currency
                )}
              </p>
              <p className="flex items-center">
                <FontAwesomeIcon
                  icon={faCreditCard}
                  className="mr-3 w-5 text-gray-500"
                />
                <span className="font-medium">Payment Status:</span>&nbsp;
                <span
                  className={
                    orderData.payment_status.awaiting_payment
                      ? "text-yellow-600"
                      : "text-green-600"
                  }
                >
                  {orderData.payment_status.awaiting_payment
                    ? "Pending"
                    : "Paid"}
                </span>
              </p>
            </div>
          </div>

          {/* Payment Details */}
          <div>
            <h2 className="text-lg font-semibold mb-3">Payment Details</h2>
            <div className="bg-gray-50 rounded p-4 space-y-2">
              <p className="text-blue-800 font-medium">
                Amount paid:{" "}
                {formatPrice(
                  calculateFinalPrice(orderData.total_amount),
                  orderData.total_currency
                )}
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 space-y-4">
            {!isNewBooking && (
              <div className="flex gap-4">
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className="flex-1 bg-red-500 text-white py-4 rounded-full hover:bg-red-600 transition-colors text-lg font-semibold"
                >
                  Cancel Trip
                </button>
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className={`flex-1 ${config.primaryColorBg} text-gray-800 py-4 rounded-full hover:bg-purple-700 transition-colors text-lg font-semibold`}
                >
                  Modify Trip
                </button>
              </div>
            )}
            <button
              onClick={() => navigate("/flights")}
              className="w-full bg-gray-100 text-gray-800 py-4 rounded-full hover:bg-gray-200 transition-colors text-lg font-semibold"
            >
              Search for More Flights
            </button>
          </div>

          {/* Support Modal */}
          {isSupportModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg w-full max-w-md p-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-bold">Contact Support</h3>
                  <button
                    onClick={() => setIsSupportModalOpen(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <p className="text-gray-600 mb-6">
                  Please contact our support team for assistance with your
                  booking:
                </p>

                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={faPhone}
                        className="text-gray-600"
                      />
                      <span>{config.supportInfo.phone}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(config.supportInfo.phone)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon
                        icon={faEnvelope}
                        className="text-gray-600"
                      />
                      <span>{config.supportInfo.email}</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(config.supportInfo.email)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </button>
                  </div>
                </div>

                <p className="text-sm text-gray-500 mt-6">
                  Our support team is available 24/7 to assist you with any
                  changes or cancellations.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default BookingConfirmation;
